// ONE renderer for a concierge⇄talent thread, shared by both pages that show one.
//
// The project's Sellers tab and a talent's own page are the same record viewed
// from opposite ends, so they must not drift into two descriptions of it. What
// differs is only the framing, and that is the props:
//
//   project page — no `heading`: the panel identifies the TALENT (avatar, linked
//                  name, @id), because the project is the page.
//   talent page  — `heading` is the PROJECT title and `action` links into that
//                  project's Explorer view, because the talent is the page.
//
// The body below the head is identical in both: status facts, the decline reason,
// SCOUT's rationale, every extracted proposal, and the full message log.
import { Badge, Card } from "@recruiter/shared";
import { useEffect, useRef, type ReactNode } from "react";

import { formatDateTime, formatMinutes, formatOfferPrice } from "../lib/format";
import {
  offerAccepted,
  threadStatus,
  type SellerMessage,
  type SellerOffer,
  type SellerThreadRow,
} from "../lib/seller-threads";

/** One labelled bullet group of an offer. Renders nothing when the talent said
 *  nothing on that axis, rather than an empty heading. */
function OfferList({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="ex-offer__list">
      <h5>{label}</h5>
      <ul>
        {items.map((item, index) => (
          <li key={`${label}-${index}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

/** One extracted proposal. Price, terms, JUNO's scores, then whatever the talent
 *  actually committed to. */
function OfferCard({ offer }: { offer: SellerOffer }) {
  return (
    <div className={`ex-offer${offer.recommended ? " ex-offer--recommended" : ""}`}>
      <div className="ex-offer__head">
        <strong className="ex-offer__price">
          {formatOfferPrice(offer.priceCents, offer.currency, offer.priceMaxCents)}
        </strong>
        {offer.timelineDays !== null ? <span>{offer.timelineDays} days</span> : null}
        {offer.revisionRounds !== null ? (
          <span>
            {offer.revisionRounds} {offer.revisionRounds === 1 ? "revision" : "revisions"}
          </span>
        ) : null}
        {/* Coloured on JUNO's OWN bar, not a number picked here. This read 70
            while the backend passed at 60, so an offer scoring 60-69 printed a
            grey "fit" chip beside a green "Offer received" badge and became a
            finalist card anyway. */}
        {offer.fitScore !== null ? (
          <Badge tone={offerAccepted(offer) ? "success" : "danger"} size="sm">
            fit {offer.fitScore}
          </Badge>
        ) : null}
        {offer.completenessScore !== null ? (
          <Badge tone="neutral" size="sm">
            complete {offer.completenessScore}
          </Badge>
        ) : null}
        {offer.recommended ? (
          <Badge tone="success" size="sm">
            Recommended
          </Badge>
        ) : null}
      </div>
      <p className="ex-offer__when">
        Received {formatDateTime(offer.receivedAt)}
        {offer.expiresAt ? `, expires ${formatDateTime(offer.expiresAt)}` : ""}
      </p>
      <OfferList label="Scope" items={offer.scopeBullets} />
      <OfferList label="Includes" items={offer.inclusions} />
      <OfferList label="Excludes" items={offer.exclusions} />
      <OfferList label="Assumptions" items={offer.assumptions} />
      <OfferList label="Risks flagged" items={offer.risksFlagged} />
    </div>
  );
}

const AUTHOR_LABEL: Record<SellerMessage["author"], string> = {
  sterling: "Sterling",
  client: "Client",
  talent: "Talent",
  unknown: "Unknown sender",
};

function MessageRow({ message, talentName }: { message: SellerMessage; talentName: string }) {
  const fromTalent = message.direction === "from_seller";
  const author = message.author === "talent" ? talentName : AUTHOR_LABEL[message.author];
  return (
    <article className={`ex-seller-msg ex-seller-msg--${fromTalent ? "in" : "out"}`}>
      <div className="ex-seller-msg__meta">
        <strong>{author}</strong>
        {/* A never-delivered draft must never read as a sent message: the flag
            is permanent, and every talent-facing read filters these out. */}
        {message.draft ? (
          <Badge tone="warning" size="sm">
            Draft, not sent
          </Badge>
        ) : null}
        {message.automated ? (
          <Badge tone="neutral" size="sm">
            Automated reply
          </Badge>
        ) : null}
        <time>{formatDateTime(message.sentAt)}</time>
      </div>
      <p>{message.body || "(empty message)"}</p>
    </article>
  );
}

export interface SellerThreadDetailProps {
  row: SellerThreadRow;
  /** DOM id, so the row button that opened this can `aria-controls` it. */
  id: string;
  /** Title for the panel. Omit to identify the TALENT instead (the project
   *  page's framing). */
  heading?: string;
  /** A control in the panel head, e.g. a link into the project. */
  action?: ReactNode;
}

/**
 * The selected thread: status, proposal, conversation.
 *
 * Brings its head into view when the selection changes. `block: "nearest"` makes
 * that a no-op when the panel is already on screen, so the only time the page
 * moves is when a click would otherwise have appeared to do nothing (a dozen rows
 * can push this below the fold).
 */
export function SellerThreadDetail({ row, id, heading, action }: SellerThreadDetailProps) {
  const status = threadStatus(row.state, row.latestOffer);
  const head = useRef<HTMLElement | null>(null);

  useEffect(() => {
    head.current?.scrollIntoView({ block: "nearest" });
  }, [row.id]);

  return (
    <Card padding="lg" className="ex-seller-detail" id={id}>
      <header className="ex-seller-detail__head" ref={head}>
        {heading === undefined ? (
          <>
            {row.avatarUrl ? <img className="ex-seller-avatar" src={row.avatarUrl} alt="" /> : null}
            <div className="ex-seller-detail__who">
              <h3 className="ad-card__title">
                {row.profileUrl ? (
                  <a
                    className="ex-primary-link"
                    href={row.profileUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {row.displayName}
                  </a>
                ) : (
                  row.displayName
                )}
              </h3>
              <p className="ex-muted">
                @{row.sellerId}
                {row.headline ? `, ${row.headline}` : ""}
              </p>
            </div>
          </>
        ) : (
          <div className="ex-seller-detail__who">
            <h3 className="ad-card__title">{heading}</h3>
          </div>
        )}
        <div className="ex-badges">
          <Badge tone={status.tone}>{status.label}</Badge>
          {row.recommended ? <Badge tone="success">Recommended</Badge> : null}
          {heading === undefined && row.isPro ? <Badge tone="accent">Pro</Badge> : null}
          {action}
        </div>
      </header>

      <dl className="ex-facts ex-seller-detail__facts">
        <div>
          <dt>Approached</dt>
          <dd>{formatDateTime(row.approachedAt)}</dd>
        </div>
        <div>
          <dt>First reply</dt>
          <dd>
            {row.firstReplyAt ? formatDateTime(row.firstReplyAt) : "No reply yet"}
            {row.repliedInMinutes !== null ? ` (${formatMinutes(row.repliedInMinutes)})` : ""}
          </dd>
        </div>
        <div>
          <dt>Last message</dt>
          <dd>{formatDateTime(row.lastMessageAt)}</dd>
        </div>
        <div>
          <dt>Standing</dt>
          <dd>
            {row.rating !== null ? `${row.rating.toFixed(1)} rating` : "Rating unknown"}
            {row.completedOrders !== null ? `, ${row.completedOrders} orders` : ""}
          </dd>
        </div>
      </dl>

      {row.declineReason ? (
        <p className="ex-seller-note ex-seller-note--decline">
          <strong>Declined:</strong> {row.declineReason.replaceAll("_", " ")}
          {row.declineDetail ? `. ${row.declineDetail}` : ""}
        </p>
      ) : null}

      {row.pickRationale ? (
        <p className="ex-seller-note">
          <strong>Why Scout picked them:</strong> {row.pickRationale}
          {row.pickDomainRelevance ? ` (${row.pickDomainRelevance})` : ""}
        </p>
      ) : null}

      <section className="ex-seller-detail__section">
        <h4 className="ex-seller-detail__title">
          Proposal
          {row.offers.length > 1 ? (
            <span className="ex-count"> {row.offers.length} versions</span>
          ) : null}
        </h4>
        {row.offers.length === 0 ? (
          <p className="ex-muted">
            No proposal yet. Nothing has been extracted from this conversation.
          </p>
        ) : (
          row.offers.map((offer) => <OfferCard key={offer.id} offer={offer} />)
        )}
      </section>

      {/* NO Fiverr OS link on this heading, deliberately, and do not add one back.
          What is rendered below is the STERLING thread: the per-project bot
          talking to the talent (ADR-0005). The Fiverr OS link is the CLIENT's own
          conversation with that talent, which is a different conversation with a
          different pair of participants — so a link here read as "open this
          conversation" and opened another one, usually empty. It lives on the ROW
          instead, in both tables that list these threads (a project's Sellers tab
          and the talent page), where it reads as a fact about the talent rather
          than as a way into the thread on screen. */}
      <section className="ex-seller-detail__section">
        <h4 className="ex-seller-detail__title">
          Conversation with Sterling
          <span className="ex-count">
            {" "}
            {row.messageCount} {row.messageCount === 1 ? "message" : "messages"}
          </span>
        </h4>
        {row.messages.length === 0 ? (
          <p className="ex-muted">No messages yet.</p>
        ) : (
          <div className="ex-seller-thread">
            {row.messages.map((message) => (
              <MessageRow key={message.id} message={message} talentName={row.displayName} />
            ))}
          </div>
        )}
      </section>
    </Card>
  );
}
